import React, { useEffect, useRef, useState } from "react";
import { buildApiUrl } from '../config';
import { formatDisplayDate } from '../utils/formatDisplayDate';
import "./Ledger.css";

const Ledger = () => {
  const [ledgerData, setLedgerData] = useState([]);         // full ledger
  const [dateFilteredData, setDateFilteredData] = useState([]); // date-range filtered
  const [filteredLedgerData, setFilteredLedgerData] = useState([]); // final data after search

  const [selectedEntity, setSelectedEntity] = useState("");
  const [entityOptions, setEntityOptions] = useState([]);
  const [entityType, setEntityType] = useState("supplier");

  // For Searching
  const [search, setSearch] = useState("");

  // For Date Filtering
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Track which row is highlighted
  const [selectedRow, setSelectedRow] = useState(-1);

  // 1) Use a ref for the table container so we can scroll it programmatically
  const containerRef = useRef(null);

  // Handle arrow key presses to move highlight up/down
  const handleKeyDown = (e) => {
    if (!filteredLedgerData.length) return;

    if (e.key === "ArrowDown") {
      e.preventDefault(); // Stop default scrolling
      setSelectedRow((prev) => Math.min(prev + 1, filteredLedgerData.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault(); // Stop default scrolling
      setSelectedRow((prev) => Math.max(prev - 1, 0));
    }
  };

  // 2) Whenever selectedRow changes, scroll the container so the row is in view
  useEffect(() => {
    if (selectedRow < 0 || !containerRef.current) return;

    const rows = containerRef.current.querySelectorAll("tbody tr");
    if (rows[selectedRow]) {
      rows[selectedRow].scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [selectedRow]);

  // Fetch entity list (companies/suppliers/customers/workers) whenever entityType changes
  const fetchEntities = async () => {
    console.log("🔍 fetchEntities called for entityType:", entityType);
    if (!entityType) return;

    try {
      let endpoint;
      if (entityType === "supplier") {
        endpoint = "suppliers";
      } else if (entityType === "company") {
        endpoint = "companiesinfo";
      } else if (entityType === "customer") {
        endpoint = "customers";
      } else if (entityType === "worker") {
        endpoint = "workers";
      } else {
        console.error("Unknown entity type:", entityType);
        return;
      }

      console.log("📡 Fetching from endpoint:", buildApiUrl(`api/${endpoint}`));
      const response = await fetch(buildApiUrl(`api/${endpoint}`));
      const data = await response.json();
      console.log("📊 Entity data received:", data);

      if (Array.isArray(data)) {
        setEntityOptions(data);
        console.log("✅ Entity options set, count:", data.length);
      } else {
        console.error("Unexpected response format:", data);
        setEntityOptions([]);
      }
    } catch (error) {
      console.error(`Error fetching ${entityType} names:`, error);
    }
  };

  // Add useEffect to fetch entities when component mounts or entityType changes
  useEffect(() => {
    console.log("🚀 Ledger component mounted, fetching entities...");
    fetchEntities();
  }, [entityType]);

  // Fetch ledger for the selected entity
  const fetchLedgerEntries = async () => {
    if (!selectedEntity) {
      alert("Please select an entity");
      return;
    }

    console.log("🔍 Fetching ledger for:", entityType, selectedEntity);
    try {
      const encodedName = encodeURIComponent(selectedEntity);
      const response = await fetch(buildApiUrl(`api/ledgerEntries?entityType=${entityType}&entityName=${encodedName}`));
      console.log("📡 Ledger API response status:", response.status);
      const data = await response.json();
      console.log("📊 Ledger data received:", data);
      
      // Check if the response is an array (successful) or an error object
      if (Array.isArray(data)) {
        setLedgerData(data);
        setDateFilteredData(data);
        setFilteredLedgerData(data);
        setSelectedRow(-1);
        console.log("✅ Ledger data set successfully");
      } else {
        // Handle error response
        console.error("❌ API returned error:", data);
        alert(`Failed to fetch ledger entries: ${data.error || 'Unknown error'}`);
        // Keep the current state (empty arrays) instead of setting error object
      }
    } catch (error) {
      console.error("❌ Error fetching ledger entries:", error);
      alert("Failed to fetch ledger entries. Check console for details.");
    }
  };

  // Convert e.g. "01-Mar-2025" to "2025-03-01" if needed
  const convertDateToISO = (dateStr) => {
    const months = {
      Jan: "01",
      Feb: "02",
      Mar: "03",
      Apr: "04",
      May: "05",
      Jun: "06",
      Jul: "07",
      Aug: "08",
      Sep: "09",
      Oct: "10",
      Nov: "11",
      Dec: "12",
    };

    const [day, month, year] = dateStr.split("-");
    if (!day || !month || !year || !months[month]) {
      // If format doesn't match, just return as-is
      return dateStr;
    }
    return `${year}-${months[month]}-${day.padStart(2, "0")}`;
  };

  // Date filter: only applied when user clicks "Filter"
  const handleDateFilter = () => {
    if (!startDate || !endDate) {
      alert("Please select both start and end dates.");
      return;
    }

    const start = new Date(startDate);
    const end = new Date(endDate);

    const filtered = ledgerData.filter((entry) => {
      const isoDate = convertDateToISO(entry.Date);
      const entryDate = new Date(isoDate);
      return entryDate >= start && entryDate <= end;
    });

    if (filtered.length === 0) {
      alert("No records found for the selected date range.");
    }

    setDateFilteredData(filtered);
  };

  // Search filter: applies automatically as user types
  useEffect(() => {
    let finalData = dateFilteredData;

    if (search.trim()) {
      const lowerSearch = search.toLowerCase();
      finalData = dateFilteredData.filter((entry) => {
        const desc = entry.Description?.toLowerCase() || "";
        return desc.includes(lowerSearch);
      });
    }

    setFilteredLedgerData(finalData);
  }, [search, dateFilteredData]);

  // Export currently displayed Ledger entries to CSV
  const exportLedgerCSV = () => {
    if (!filteredLedgerData || filteredLedgerData.length === 0) {
      alert("No data to export");
      return;
    }

    const headers = [
      'Date',
      'Description',
      'Debit',
      'Credit',
      'Balance'
    ];

    const rows = filteredLedgerData.map((entry) => [
      entry.Date ? formatDisplayDate(entry.Date) : '-',
      entry.Description || '-',
      entry.Debit ? String(entry.Debit) : '-',
      entry.Credit ? String(entry.Credit) : '-',
      entry.Balance !== undefined ? String(entry.Balance) : '-'
    ]);

    const csv = [headers, ...rows]
      .map(row => row
        .map(cell => {
          const val = cell === null || cell === undefined ? '' : String(cell);
          const escaped = val.replace(/"/g, '""');
          return /[",\n]/.test(escaped) ? `"${escaped}"` : escaped;
        })
        .join(','))
      .join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const today = new Date().toISOString().split('T')[0];
    const safeEntity = selectedEntity ? selectedEntity.replace(/[^a-z0-9_-]+/gi, '_') : 'entity';
    link.download = `Ledger_${entityType}_${safeEntity}_${today}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="ledger-container">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
        <h1 style={{ margin: 0 }}>Ledger</h1>
        <button
          onClick={exportLedgerCSV}
          style={{
            background: 'none',
            border: '1px solid #ccc',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '18px',
            color: '#217346',
            padding: '6px 10px',
            display: 'flex',
            alignItems: 'center'
          }}
          title="Export current table to CSV"
        >
          📊
        </button>
      </div>

      <div className="ledger-selector">
        {/* Entity Type Dropdown */}
        <div className="dropdown-container">
          <label htmlFor="entityTypeSelect">Select Entity Type:</label>
          <select
            id="entityTypeSelect"
            value={entityType}
            onChange={(e) => setEntityType(e.target.value)}
          >
            <option value="supplier">Supplier</option>
            <option value="company">Company</option>
            <option value="customer">Customer</option>
            <option value="worker">Worker</option>
          </select>
        </div>

        {/* Entity Dropdown */}
        <div className="dropdown-container">
          <label htmlFor="entitySelect">Select Entity:</label>
          <select
            id="entitySelect"
            value={selectedEntity}
            onChange={(e) => setSelectedEntity(e.target.value)}
          >
            <option value="">--Select Entity--</option>
            {entityOptions.map((entity, index) => (
              <option key={entity.id || index} value={entity.name}>
                {entity.name}
              </option>
            ))}
          </select>
        </div>

        <div className="dropdown-container">
        {/* <label htmlFor="entitySelect" style={{ color: "white" }}>Ledger generator</label> */}

        <button
  id="generateLedgerBtn"
  onClick={fetchLedgerEntries}
  style={{ marginTop: "25px" }}
>
  Generate Ledger
</button>


        </div>

      </div>

      {/* Show date/search filters only if we have ledger data */}
      {ledgerData.length > 0 && (
        <div className="filter-container">
          {/* Search Bar (Immediate Filter) */}
          <div className="search-container">
            <label>Search:</label>
            <input
              type="text"
              placeholder="Search description..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          {/* Date Range Filter (Apply on Button Click) */}
          <div className="date-filter-container">
            <label>Start Date:</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
            <label>End Date:</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
            <button onClick={handleDateFilter} className="filter-button">
              Filter
            </button>
          </div>
        </div>
      )}

      {/* Make table container focusable & handle arrow keys */}
      <div
        className="entries-table-container"
        tabIndex="0"
        ref={containerRef}
        onKeyDown={handleKeyDown}
      >
        <table className="entries-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Description</th>
              <th>Debit</th>
              <th>Credit</th>
              <th>Balance</th>
            </tr>
          </thead>
          <tbody>
            {filteredLedgerData.length === 0 ? (
              <tr>
                <td colSpan="5" style={{ textAlign: "center" }}>
                  No entries found.
                </td>
              </tr>
            ) : (
              filteredLedgerData.map((entry, index) => {
                const description = entry.Description || "-";

                return (
                  <tr
                    key={index}
                    onClick={() => setSelectedRow(index)}
                    className={index === selectedRow ? "highlighted-row" : ""}
                  >
                    <td>{formatDisplayDate(entry.Date)}</td>
                    <td>{description}</td>
                    <td>{entry.Debit ? entry.Debit : "-"}</td>
                    <td>{entry.Credit ? entry.Credit : "-"}</td>
                    <td>{entry.Balance}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Ledger;
